import React, {useState, useEffect} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {GraphiQL} from "graphiql";
import "graphiql/graphiql.css";

import "./TopicDescriptionCreation.css";
import {Resolution} from "../../../logic/API";
import {
  AuthenticationBasic,
  AuthenticationToken,
  AuthMethod,
  TopicDescription,
  TopicDescriptionGraphQLTransfer,
} from "../../../logic/APITypes";
import {
  DropDown,
  NumberInput,
  TextInput,
  DropDownOption,
} from "@spg/component_library";
import {DefaultInputBehavior} from "../../../reusables/DefaultInputBehavior";
import {AuthenticationSubSection} from "./AuthenticationSubSection";
import {createGraphiQLFetcher} from "../../../logic/GraphiQL";

/**
 * Controls the transfer section when creating, viewing and editing graphQL topic descriptions.
 */
function GraphQLTransferSection(props: {
  alternative: "create" | "view" | "edit";
  waitingForResponse: boolean;
  pullTransferInitTrigger: number;
  pullTransferNullTrigger: number;
  setPullInfo: (
    unmodifiedInputs: boolean,
    validInputs: boolean,
    strategy: TopicDescription["information_strategy"],
    transfer: TopicDescriptionGraphQLTransfer,
  ) => void;
  initialTopic: Resolution<TopicDescription> | undefined;
  authentication: AuthMethod;
  setAuthentication: (authentication: AuthMethod) => void;
}) {
  const intl = useIntl();

  // States for tracking form contents and potential errors
  const strategyOptions: DropDownOption<
    TopicDescription["information_strategy"]
  >[] = [
    {
      value: "EMAIL",
      label: intl.formatMessage({
        id: "topicDescriptionCreation.option.strategy.EMAIL",
      }),
    },
    {
      value: "SMS",
      label: intl.formatMessage({
        id: "topicDescriptionCreation.option.strategy.SMS",
      }),
    },
  ];
  const [inputs, setInputs] = useState({
    name: "",
    secret: "",
    interval: "900",
    url: "",
    query: "",
    variables: "",
    headers: "",
    strategy: null as null | DropDownOption<
      TopicDescription["information_strategy"]
    >,
  });
  let fetcherUrl;
  try {
    fetcherUrl = new URL(inputs.url);
    if (props.authentication === "TOKEN_QUERY" && inputs.name && inputs.secret)
      fetcherUrl.searchParams.append(inputs.name, inputs.secret);
  } catch {
    // ignore incomplete/invalid URLs
  }
  const fetcher = createGraphiQLFetcher({
    url: fetcherUrl ? fetcherUrl.toString() : "/",
  });

  const initialTopic = props.initialTopic?.payload;
  const placeholder = props.initialTopic?.unresolved
    ? intl.formatMessage({
        id: "topicDescriptionCreation.inputLabel.placeholder.loading",
      })
    : undefined;

  /** Initializes form with values based on the initialTopic if available,
   * otherwise only info available from the overview table is filled in.
   * For the creation alternatives default values (mostly empty) are used. */
  const setInitialStates = () => {
    setInputs({
      url:
        (initialTopic?.transfer as TopicDescriptionGraphQLTransfer | undefined)
          ?.url || "",
      query:
        (initialTopic?.transfer as TopicDescriptionGraphQLTransfer | undefined)
          ?.query || "",
      variables:
        JSON.stringify(
          (
            initialTopic?.transfer as
              | TopicDescriptionGraphQLTransfer
              | undefined
          )?.variables,
        ) || "",
      interval:
        (
          initialTopic?.transfer as TopicDescriptionGraphQLTransfer | undefined
        )?.interval.toString() || "900",
      strategy:
        strategyOptions.find(
          (dfo) => dfo.value === initialTopic?.information_strategy,
        ) || null,
      name: "",
      secret: "",
      headers: "",
    });
    resetHeaders();
  };

  const resetHeaders = () => {
    const headers =
      props.authentication === "TOKEN_HEADER"
        ? `{"${inputs.name}": "${inputs.secret}"}`
        : props.authentication === "BASIC"
          ? `{"Authorization": "Basic ${btoa(
              Array.from(
                new TextEncoder().encode(inputs.name + ":" + inputs.secret),
                (byte) => String.fromCodePoint(byte),
              ).join(""),
            )}"}`
          : "";
    if (headers != inputs.headers) {
      setInputs((oldInputs) => ({
        ...oldInputs,
        headers,
      }));
    }
  };

  useEffect(() => {
    if (props.pullTransferInitTrigger) setInitialStates();
  }, [props.pullTransferInitTrigger]);

  useEffect(() => {
    if (props.pullTransferNullTrigger) {
      setInputs({
        name: "",
        secret: "",
        interval: "900",
        url: "",
        query: "",
        variables: "",
        headers: "",
        strategy: null,
      });
    }
  }, [props.pullTransferNullTrigger]);

  useEffect(() => {
    resetHeaders();
    if (!inputs.strategy || initialTopic?.transfer.direction === "PUSH") return;
    let authentication:
      | AuthenticationBasic
      | AuthenticationToken
      | {method: AuthMethod};
    if (
      props.authentication === "BASIC" &&
      inputs.name !== "" &&
      inputs.secret !== ""
    ) {
      authentication = {
        username: inputs.name,
        password: inputs.secret,
        method: "BASIC",
      };
    } else if (
      (props.authentication === "TOKEN_HEADER" ||
        props.authentication === "TOKEN_QUERY") &&
      inputs.name !== "" &&
      inputs.secret !== ""
    ) {
      authentication = {
        token: inputs.secret,
        name: inputs.name,
        method: props.authentication,
      };
    } else {
      authentication = {
        method: props.authentication,
      };
    }
    let variableDict = {};
    let invalidVariables = false;
    let variablesString = inputs.variables;
    if (variablesString.trim() == "") variablesString = "{}";
    try {
      variableDict = JSON.parse(variablesString);
    } catch {
      invalidVariables = true;
    }
    const transfer: TopicDescriptionGraphQLTransfer = {
      transfer_type: "GRAPHQL",
      direction: "PULL",
      authentication: authentication,
      interval: parseInt(inputs.interval),
      url: inputs.url,
      query: inputs.query,
      variables: variableDict,
    };

    const unmodifiedInputs =
      initialTopic?.transfer.authentication?.method === props.authentication &&
      inputs.name === "" &&
      inputs.secret === "" &&
      parseInt(inputs.interval) === initialTopic?.transfer.interval &&
      inputs.url === initialTopic?.transfer.url &&
      "query" in initialTopic.transfer &&
      inputs.query === initialTopic?.transfer.query &&
      JSON.stringify(variableDict) ===
        JSON.stringify(initialTopic?.transfer.variables) &&
      inputs.strategy?.value === initialTopic?.information_strategy;

    const validInputs =
      ((inputs.name.length === 0 && inputs.secret.length === 0) ||
        (inputs.name.length > 0 && inputs.secret.length > 0)) &&
      inputs.url.length > 0 &&
      inputs.query.length > 0 &&
      !invalidVariables &&
      !isNaN(parseInt(inputs.interval)) &&
      10 <= parseInt(inputs.interval) &&
      parseInt(inputs.interval) <= 1000000 &&
      inputs.strategy;

    props.setPullInfo(
      unmodifiedInputs,
      validInputs as boolean,
      inputs.strategy?.value || "EMAIL",
      transfer,
    );
  }, [inputs]);

  return (
    <>
      <div className="topicDescriptionCreationIntervalContainer">
        <DefaultInputBehavior
          component={NumberInput}
          label={intl.formatMessage({
            id: "topicDescriptionCreation.inputLabel.interval",
          })}
          placeholder={placeholder || ""}
          idBase={"topicDescriptionCreationInterval"}
          value={inputs.interval}
          setValue={(interval) => {
            setInputs((oldInputs) => ({
              ...oldInputs,
              interval,
            }));
          }}
          minValue={10}
          maxValue={1000000}
          isRequired
          isDisabled={props.waitingForResponse}
          isReadOnly={
            props.alternative === "view" || props.initialTopic?.unresolved
          }
          isEdited={
            initialTopic &&
            initialTopic.transfer.direction === "PULL" &&
            initialTopic?.transfer.interval !== parseInt(inputs.interval)
          }
        />
      </div>
      <div className="topicDescriptionCreationStrategyContainer">
        <DefaultInputBehavior
          component={DropDown}
          label={intl.formatMessage({
            id: "topicDescriptionCreation.inputLabel.strategy",
          })}
          placeholder={
            placeholder ||
            intl.formatMessage({
              id: "topicDescriptionCreation.inputLabel.strategy",
            })
          }
          idBase={"topicDescriptionCreationStrategy"}
          options={strategyOptions}
          selectedOption={inputs.strategy}
          setSelectedOption={(strategy) =>
            setInputs((oldInputs) => ({
              ...oldInputs,
              strategy: strategy as typeof inputs.strategy,
            }))
          }
          isRequired
          isDisabled={props.waitingForResponse}
          isReadOnly={
            props.alternative === "view" || props.initialTopic?.unresolved
          }
          isEdited={
            (initialTopic &&
              inputs.strategy &&
              initialTopic?.information_strategy !==
                inputs.strategy.value) as boolean
          }
        />
      </div>
      <div className="topicDescriptionCreationSpecificsContainer">
        <DefaultInputBehavior
          component={TextInput}
          label={intl.formatMessage({
            id: "topicDescriptionCreation.inputLabel.URL",
          })}
          placeholder={placeholder || ""}
          idBase={"topicDescriptionCreationURL"}
          value={inputs.url}
          setValue={(url) =>
            setInputs((oldInputs) => ({
              ...oldInputs,
              url,
            }))
          }
          isRequired
          isDisabled={props.waitingForResponse}
          isReadOnly={
            props.alternative === "view" || props.initialTopic?.unresolved
          }
          isEdited={
            initialTopic &&
            initialTopic.transfer.direction === "PULL" &&
            initialTopic?.transfer.url !== inputs.url
          }
        />
      </div>
      <AuthenticationSubSection
        alternative={props.alternative}
        waitingForResponse={props.waitingForResponse}
        authentication={{
          get: props.authentication,
          set: props.setAuthentication,
        }}
        name={{
          get: inputs.name,
          set: (name) =>
            setInputs((oldInputs) => ({
              ...oldInputs,
              name,
            })),
        }}
        secret={{
          get: inputs.secret,
          set: (secret) =>
            setInputs((oldInputs) => ({
              ...oldInputs,
              secret,
            })),
        }}
        initialTopic={undefined}
      />
      <div className="topicDescriptionCreationQueryContainer">
        {fetcher && (
          <GraphiQL
            fetcher={fetcher}
            onEditQuery={(query) => {
              if (query == inputs.query) return;
              setInputs((oldInputs) => ({
                ...oldInputs,
                query,
              }));
            }}
            onEditVariables={(variables) => {
              if (variables == inputs.variables) return;
              setInputs((oldInputs) => ({
                ...oldInputs,
                variables,
              }));
            }}
            readOnly={
              props.alternative === "view" || props.initialTopic?.unresolved
            }
            disableTabs
            query={inputs.query}
            variables={inputs.variables}
            headers={inputs.headers}
          >
            <GraphiQL.Logo>
              <FormattedMessage id="topicDescriptionCreation.inputLabel.query" />
            </GraphiQL.Logo>
          </GraphiQL>
        )}
      </div>
    </>
  );
}

export {GraphQLTransferSection};
